import React, { useState, useEffect } from 'react';
import API from '../../API';

const EventDefaults = ({ accessToken }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [contentLoadUrl, setContentLoadUrl] = useState('');
  const [upcUrl, setUpcUrl] = useState('');
  const [umsUri, setUmsUri] = useState('');
  const [upcWorldMapContentUrl, setUpcWorldMapContentUrl] = useState('');
  const [umsWorldMapContentUrl, setUmsWorldMapContentUrl] = useState('');
  const [zoomMax, setZoomMax] = useState('');
  const [defaultZoomEval, setDefaultZoomEval] = useState('');
  const [audibleDistance, setAudibleDistance] = useState('');
  const [conversationalDistance, setConversationalDistance] = useState('');
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    async function fetchDefaults() {
      if (!accessToken) return;

      setLoading(true);
      setError(null);
      try {
        const response = await API.adminGetEventDefaults(accessToken);
        if (response.ok) {
          const data = await response.json();
          setTitle(data.title || '');
          setSubtitle(data.subtitle || '');
          setContentLoadUrl(data.content_load_url || '');
          setUpcUrl(data.upc_url || '');
          setUmsUri(data.ums_uri || '');
          setUpcWorldMapContentUrl(data.upc_world_map_content_url || '');
          setUmsWorldMapContentUrl(data.ums_world_map_content_url || '');
          setZoomMax(data.zoom_max || '');
          setDefaultZoomEval(data.default_zoom_eval || '');
          setAudibleDistance(data.audible_distance || '');
          setConversationalDistance(data.conversational_distance || '');
          setHidden(data.hidden || false);
        } else {
          setError('Failed to load event defaults');
        }
      } catch (err) {
        setError(`Failed to load event defaults: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }

    fetchDefaults();
  }, [accessToken]);

  const handleSave = async () => {
    if (!accessToken) return;

    setSaving(true);
    setError(null);
    try {
      const response = await API.adminUpdateEventDefaults(accessToken, {
        title,
        subtitle,
        content_load_url: contentLoadUrl,
        upc_url: upcUrl,
        ums_uri: umsUri,
        upc_world_map_content_url: upcWorldMapContentUrl,
        ums_world_map_content_url: umsWorldMapContentUrl,
        zoom_max: zoomMax,
        default_zoom_eval: defaultZoomEval,
        audible_distance: audibleDistance,
        conversational_distance: conversationalDistance,
        hidden
      });

      if (response.ok || response.status === 204) {
        window.alert('Event defaults saved successfully!');
      } else {
        const errorText = await response.text();
        setError(`Failed to save: ${errorText || response.statusText}`);
      }
    } catch (err) {
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleInputFocus = (e) => {
    const input = e.target;
    if (input.setSelectionRange) {
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }
    input.scrollLeft = input.scrollWidth;
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 border-2 border-white rounded-3xl p-6 bg-gray-900/50">
        <div className="text-center py-8 text-gray-400">Loading event defaults...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 border-2 border-white rounded-3xl p-6 bg-gray-900/50">
      <h3 className="text-xl font-semibold m-0">Event Defaults</h3>
      <p className="text-gray-400 text-sm -mt-4">
        These values will be used as defaults when creating new events.
      </p>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/50 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <label
            htmlFor="default-title"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Title:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-subtitle"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Subtitle:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-subtitle"
            type="text"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-content-load-url"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Content Load URL:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-content-load-url"
            type="text"
            value={contentLoadUrl}
            onChange={(e) => setContentLoadUrl(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-upc-url"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            UPC URL:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-upc-url"
            type="text"
            value={upcUrl}
            onChange={(e) => setUpcUrl(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-ums-uri"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            UMS URI:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-ums-uri"
            type="text"
            value={umsUri}
            onChange={(e) => setUmsUri(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-upc-world-map"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            UPC World Map URL:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-upc-world-map"
            type="text"
            value={upcWorldMapContentUrl}
            onChange={(e) => setUpcWorldMapContentUrl(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-ums-world-map"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            UMS World Map URL:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-ums-world-map"
            type="text"
            value={umsWorldMapContentUrl}
            onChange={(e) => setUmsWorldMapContentUrl(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-zoom-max"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Zoom Max:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-zoom-max"
            type="text"
            value={zoomMax}
            onChange={(e) => setZoomMax(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-zoom-eval"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Default Zoom Eval:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-zoom-eval"
            type="text"
            value={defaultZoomEval}
            onChange={(e) => setDefaultZoomEval(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-audible-distance"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Audible Distance:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-audible-distance"
            type="text"
            value={audibleDistance}
            onChange={(e) => setAudibleDistance(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-conversational-distance"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Conversational Distance:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id="default-conversational-distance"
            type="text"
            value={conversationalDistance}
            onChange={(e) => setConversationalDistance(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="default-hidden"
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Private:
          </label>
          <input
            className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-blue-600 focus:ring-2 focus:ring-blue-500"
            id="default-hidden"
            type="checkbox"
            checked={hidden}
            onChange={(e) => setHidden(e.target.checked)}
          />
        </div>
      </div>

      <div className="flex justify-center mt-4">
        <button
          className="ff-button text-sm px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Defaults'}
        </button>
      </div>
    </div>
  );
};

export default EventDefaults;

